import React, { useState } from 'react';
import { toast } from 'sonner';
import { Video } from 'lucide-react';
import { Textarea } from '../ui/textarea';
import { Card, LockedNotice, Pill, PrimaryButton, SectionHeader } from './ui';
import { studentApi } from '../../pages/journey/journeyApi';

const FIELDS = [['planned', "This week's planned activities"], ['completed', 'Work completed'], ['pending', 'Work pending'],
  ['blockers', 'Challenges and blockers'], ['support_requested', 'Support requested'], ['reflection', 'Personal progress reflection']];

const History = ({ list }) => list.map((c) => (
  <Card key={c.id} data-testid={`checkin-week-${c.week}`}>
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <p className="font-outfit font-semibold text-[#0b1f4d]">Week {c.week}</p>
      <Pill className={c.status === 'reviewed' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-50 text-amber-700'} data-testid={`checkin-status-${c.week}`}>{c.status === 'reviewed' ? `Reviewed by ${c.coach}` : 'Awaiting coach'}</Pill>
      {c.meet_link && <a href={c.meet_link} target="_blank" rel="noreferrer" className="text-xs text-teal-700 inline-flex items-center gap-1"><Video className="w-3.5 h-3.5" /> Google Meet</a>}
    </div>
    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">{FIELDS.map(([k, l]) => c[k] && <p key={k}><span className="text-slate-500 text-xs block">{l}</span>{c[k]}</p>)}</div>
    {c.status === 'reviewed' && <div className="mt-4 rounded-xl bg-emerald-50 p-4 text-sm">
      {c.observations && <p><strong>Coach observations:</strong> {c.observations}</p>}
      {c.next_actions?.length > 0 && <ul className="mt-2">{c.next_actions.map((a) => <li key={a}>→ {a}</li>)}</ul>}
    </div>}
  </Card>
));

export const Checkins = ({ data: d, studentId, refresh }) => {
  const [form, setForm] = useState({ confidence: 3 });
  const [busy, setBusy] = useState(false);
  const sup = d.support;
  if (!sup) return (<><SectionHeader title="Weekly Check-ins" /><LockedNotice testid="checkins-locked" title="Included with enrolled programs" text="Structured weekly check-ins with a coach run during your program's active support period." /></>);
  const submit = async () => {
    setBusy(true);
    try { await studentApi('/me/checkins', { method: 'POST', body: { student_id: studentId, ...form } }); toast.success('Check-in submitted'); setForm({ confidence: 3 }); refresh(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <>
      <SectionHeader eyebrow={sup.active ? `Week ${sup.current_week} of ${sup.weeks}` : 'Support period ended'} title="Weekly Check-ins">
        Active support until {new Date(sup.end).toLocaleDateString('en-IN')}. Meetings happen on Google Meet. Your learning modules stay available after this period.
      </SectionHeader>
      {sup.active && <Card className="mb-6" data-testid="checkin-form">
        <div className="grid md:grid-cols-2 gap-4">
          {FIELDS.map(([k, l]) => (<label key={k} className="text-sm"><span className="text-slate-600">{l}</span>
            <Textarea rows={2} value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1" data-testid={`checkin-${k}-input`} /></label>))}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
          <label className="text-sm flex items-center gap-3">Confidence this week
            <input type="range" min="1" max="5" value={form.confidence} onChange={(e) => setForm({ ...form, confidence: +e.target.value })} data-testid="checkin-confidence-input" />
            <span className="font-semibold">{form.confidence}/5</span></label>
          <PrimaryButton onClick={submit} disabled={busy || !form.completed} data-testid="checkin-submit-btn">Submit check-in</PrimaryButton>
        </div>
      </Card>}
      <div className="space-y-4"><History list={d.checkins} /></div>
    </>
  );
};
